/**
 * Utility functions for creating disabled date ranges for date pickers
 */

export interface DateRangeCriteria {
  before?: {
    type: 'fixed' | 'relative'
    value: Date | { years?: number; months?: number; days?: number }
  }
  after?: {
    type: 'fixed' | 'relative'
    value: Date | { years?: number; months?: number; days?: number }
  }
}

/**
 * Creates a disabled date range object for date pickers
 * @param criteria - Object defining the before and after date constraints
 * @returns Object with before and after dates for disabled prop
 */
export function createDisabledDateRange(criteria: DateRangeCriteria) {
  const currentDate = new Date()

  const calculateDate = (
    constraint: DateRangeCriteria['before'] | DateRangeCriteria['after']
  ) => {
    if (!constraint) return undefined

    if (constraint.type === 'fixed') {
      return constraint.value as Date
    }

    if (constraint.type === 'relative') {
      const relativeValue = constraint.value as {
        years?: number
        months?: number
        days?: number
      }
      const result = new Date(currentDate)

      if (relativeValue.years) {
        result.setFullYear(result.getFullYear() + relativeValue.years)
      }
      if (relativeValue.months) {
        result.setMonth(result.getMonth() + relativeValue.months)
      }
      if (relativeValue.days) {
        result.setDate(result.getDate() + relativeValue.days)
      }

      return result
    }

    return undefined
  }

  const before = calculateDate(criteria.before)
  const after = calculateDate(criteria.after)

  // Return undefined if no constraints are provided
  if (!before && !after) {
    return undefined
  }

  // Return the constraints that are defined
  if (before && after) {
    return { before, after }
  } else if (before) {
    return { before }
  } else if (after) {
    return { after }
  }

  return undefined
}

/**
 * Helper function to create a date range with years offset from current date
 * @param yearsBefore - Number of years before current date to disable
 * @param yearsAfter - Number of years after current date to disable
 * @returns Disabled date range object
 */
export function createYearsRange(yearsBefore?: number, yearsAfter?: number) {
  const criteria: DateRangeCriteria = {}

  if (yearsBefore !== undefined) {
    criteria.before = {
      type: 'relative',
      value: { years: -yearsBefore },
    }
  }

  if (yearsAfter !== undefined) {
    criteria.after = {
      type: 'relative',
      value: { years: yearsAfter },
    }
  }

  return createDisabledDateRange(criteria)
}

/**
 * Helper function to create date picker props with disabled range and month range
 * @param yearsBefore - Number of years before current date to disable
 * @param yearsAfter - Number of years after current date to disable
 * @returns Object with disabled, startMonth, and endMonth props
 */
export function createDatePickerProps(
  yearsBefore?: number,
  yearsAfter?: number
) {
  const currentDate = new Date()

  const startMonth =
    yearsBefore !== undefined
      ? new Date(
          currentDate.getFullYear() - yearsBefore,
          currentDate.getMonth(),
          currentDate.getDate()
        )
      : undefined

  const endMonth =
    yearsAfter !== undefined
      ? new Date(
          currentDate.getFullYear() + yearsAfter,
          currentDate.getMonth(),
          currentDate.getDate()
        )
      : undefined

  return {
    disabled: createYearsRange(yearsBefore, yearsAfter),
    startMonth,
    endMonth,
  }
}

/**
 * Helper function to create date picker props with fixed date range
 * @param beforeDate - Fixed date to disable before
 * @param afterDate - Fixed date to disable after
 * @returns Object with disabled, startMonth, and endMonth props
 */
export function createFixedDatePickerProps(
  beforeDate?: Date,
  afterDate?: Date
) {
  return {
    disabled: createFixedRange(beforeDate, afterDate),
    startMonth: beforeDate,
    endMonth: afterDate,
  }
}

/**
 * Helper function to create a date range with fixed dates
 * @param beforeDate - Fixed date to disable before
 * @param afterDate - Fixed date to disable after
 * @returns Disabled date range object
 */
export function createFixedRange(beforeDate?: Date, afterDate?: Date) {
  const criteria: DateRangeCriteria = {}

  if (beforeDate) {
    criteria.before = {
      type: 'fixed',
      value: beforeDate,
    }
  }

  if (afterDate) {
    criteria.after = {
      type: 'fixed',
      value: afterDate,
    }
  }

  return createDisabledDateRange(criteria)
}

export function calculateBenefitExpiryDate(
  period: number,
  startDate?: string
): string {
  let baseDate: Date

  if (startDate) {
    const [day, month, year] = startDate.split('/')
    if (!day || !month || !year || isNaN(Number(day))) {
      baseDate = new Date()
    } else {
      baseDate = new Date(Number(year), Number(month) - 1, Number(day))
    }
    if (isNaN(baseDate.getTime())) baseDate = new Date()
  } else {
    baseDate = new Date()
  }

  const expiryDate = new Date(baseDate)
  expiryDate.setDate(baseDate.getDate() + period)
  const dd = String(expiryDate.getDate()).padStart(2, '0')
  const mm = String(expiryDate.getMonth() + 1).padStart(2, '0')
  const yyyy = expiryDate.getFullYear()
  return `${dd}-${mm}-${yyyy}`
}

export function getDaysAndHoursAndMinutes(
  value: number,
  unit: 'milliseconds' | 'seconds' | 'minutes' | 'hours' | 'days',
  stopAt?: 'days' | 'hours' | 'minutes' | 'seconds'
): { days: number; hours: number; minutes: number; seconds: number } {
  const multipliers = {
    milliseconds: 1,
    seconds: 1000,
    minutes: 60 * 1000,
    hours: 60 * 60 * 1000,
    days: 24 * 60 * 60 * 1000,
  }

  const totalMilliseconds = value * multipliers[unit]

  // If stopAt is not provided, use the original behavior
  if (!stopAt) {
    const days = Math.floor(totalMilliseconds / (24 * 60 * 60 * 1000))
    const hours = Math.floor(
      (totalMilliseconds % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)
    )
    const minutes = Math.floor(
      (totalMilliseconds % (60 * 60 * 1000)) / (60 * 1000)
    )
    const seconds = Math.floor((totalMilliseconds % (60 * 1000)) / 1000)

    return { days, hours, minutes, seconds }
  }

  // When stopAt is provided, stop extraction at that unit and add higher units to it
  let days = 0
  let hours = 0
  let minutes = 0
  let seconds = 0

  if (stopAt === 'days') {
    days = Math.floor(totalMilliseconds / (24 * 60 * 60 * 1000))
    hours = Math.floor(
      (totalMilliseconds % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)
    )
    minutes = Math.floor((totalMilliseconds % (60 * 60 * 1000)) / (60 * 1000))
    seconds = Math.floor((totalMilliseconds % (60 * 1000)) / 1000)
  } else if (stopAt === 'hours') {
    // Convert days to hours, don't extract days
    const totalHours = Math.floor(totalMilliseconds / (60 * 60 * 1000))
    hours = totalHours
    minutes = Math.floor((totalMilliseconds % (60 * 60 * 1000)) / (60 * 1000))
    seconds = Math.floor((totalMilliseconds % (60 * 1000)) / 1000)
  } else if (stopAt === 'minutes') {
    // Convert days and hours to minutes, don't extract days or hours
    const totalMinutes = Math.floor(totalMilliseconds / (60 * 1000))
    minutes = totalMinutes
    seconds = Math.floor((totalMilliseconds % (60 * 1000)) / 1000)
  } else if (stopAt === 'seconds') {
    // Convert everything to seconds, don't extract days, hours, or minutes
    seconds = Math.floor(totalMilliseconds / 1000)
  }

  return { days, hours, minutes, seconds }
}
